<div class="pdf-viewer-container">
  <!-- Full PDF Viewer -->
  <div
    class="full-pdf-viewer"
    (mousedown)="!isGridMode && $event.preventDefault()"
    (click)="$event.stopPropagation()"
  >
    <!-- mousedown and click are blocked to prevent stealing focus from input elements -->
    <!-- PDF Controls -->
    <div class="pdf-controls" *ngIf="mergedConfig.showControls">
      <!-- Navigation Controls -->
      <div class="pdf-navigation" *ngIf="mergedConfig.showNavigation">
        <button
          mat-icon-button
          (click)="previousPage()"
          [disabled]="!canPrevPage || pdfLoading"
          matTooltip="Previous Page"
          tabindex="-1"
        >
          <mat-icon>navigate_before</mat-icon>
        </button>
        <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
        <button
          mat-icon-button
          (click)="nextPage()"
          [disabled]="!canNextPage || pdfLoading"
          matTooltip="Next Page"
          tabindex="-1"
        >
          <mat-icon>navigate_next</mat-icon>
        </button>
      </div>

      <!-- Zoom Controls -->
      <div class="pdf-zoom-controls" *ngIf="mergedConfig.showZoomControls">
        <button
          mat-icon-button
          (click)="zoomOut()"
          [disabled]="!canZoomOut || pdfLoading"
          matTooltip="Zoom Out"
          tabindex="-1"
        >
          <mat-icon>zoom_out</mat-icon>
        </button>

        <span class="zoom-info" (click)="resetZoom()" matTooltip="Reset zoom to 100%">
          {{ (currentScale * 100).toFixed(0) }}%
        </span>

        <button
          mat-icon-button
          (click)="zoomIn()"
          [disabled]="!canZoomIn || pdfLoading"
          matTooltip="Zoom In"
          tabindex="-1"
        >
          <mat-icon>zoom_in</mat-icon>
        </button>

        <button
          mat-icon-button
          (click)="zoomToFit()"
          [disabled]="pdfLoading"
          matTooltip="Zoom To Fit"
          tabindex="-1"
        >
          <mat-icon>aspect_ratio</mat-icon>
        </button>
      </div>

      <!-- Action Controls -->
      <div class="pdf-action-controls" *ngIf="mergedConfig.showActionControls">
        <button
          mat-icon-button
          (click)="rotateCounterClockwise()"
          [disabled]="pdfLoading"
          matTooltip="Rotate Left"
          tabindex="-1"
        >
          <mat-icon>rotate_left</mat-icon>
        </button>
        <button
          mat-icon-button
          (click)="rotateClockwise()"
          [disabled]="pdfLoading"
          matTooltip="Rotate Right"
          tabindex="-1"
        >
          <mat-icon>rotate_right</mat-icon>
        </button>

        <!-- File Controls -->
        <div class="pdf-file-controls">
          <button
            mat-icon-button
            *ngIf="mergedConfig.showDownloadPdf"
            (click)="downloadPdf()"
            [disabled]="pdfLoading || !pdfUrl"
            matTooltip="Download PDF"
            tabindex="-1"
          >
            <mat-icon>download</mat-icon>
          </button>
          <button
            mat-icon-button
            *ngIf="mergedConfig.showOpenInNewWindow"
            (click)="openInNewWindow()"
            [disabled]="pdfLoading || !pdfUrl"
            matTooltip="Open in New Window"
            tabindex="-1"
          >
            <mat-icon>open_in_new</mat-icon>
          </button>
        </div>

        <!-- Minimize Control -->
        <div class="pdf-minimize-controls">
          <button
            mat-icon-button
            *ngIf="mergedConfig.showMinimizePdf"
            (click)="minimizePdfViewer()"
            matTooltip="Minimize PDF Viewer (F9)"
            [disabled]="pdfLoading"
            tabindex="-1"
          >
            <mat-icon>minimize</mat-icon>
          </button>
        </div>

        <!-- Grid Mode Panel -->
        <div
          *ngIf="isGridMode"
          class="grid-mode-panel"
          #gridModePanel
          [ngStyle]="gridModePanelInlineStyles"
        >
          <div class="grid-controls">
            <p-button
              icon="pi pi-download"
              [label]="isGridExtracting ? 'Extracting...' : 'Extract Grid'"
              severity="success"
              size="small"
              (click)="extractGridDataAndShowPanel()"
              [disabled]="isGridExtracting || !hasGrid()"
              styleClass="extract-btn"
              tabindex="-1"
            ></p-button>
            <p-button
              icon="pi pi-times"
              label="Clear Grid"
              severity="secondary"
              [outlined]="true"
              size="small"
              (click)="clearGrid()"
              [disabled]="isGridExtracting"
              styleClass="clear-btn"
              tabindex="-1"
            ></p-button>
          </div>
          <div class="grid-instructions">
            <mat-icon>info</mat-icon>
            <span *ngIf="!hasGrid()"
              >Draw a rectangle over the table first, then click "Extract Grid"</span
            >
            <span *ngIf="hasGrid()"
              >Shift+Click inside the box for horizontal lines, Ctrl+Click for vertical. Right-click a
              line to delete it.</span
            >
          </div>
        </div>
      </div>

      <!-- Grid Mode Controls -->
      <div class="pdf-grid-controls">
        <button
          mat-icon-button
          (click)="toggleGridMode()"
          [class.active]="isGridMode"
          [disabled]="pdfLoading || (mergedConfig.disableGridMode && !isGridMode)"
          [matTooltip]="
            mergedConfig.disableGridMode && !isGridMode
              ? 'Grid Mode disabled for this supplier'
              : isGridMode
                ? 'Exit Grid Mode'
                : 'Enable Grid Mode'
          "
          tabindex="-1"
        >
          <mat-icon>{{ isGridMode ? 'grid_off' : 'grid_on' }}</mat-icon>
        </button>
        <div *ngIf="isOcrProcessing" class="ocr-processing-indicator">
          <mat-spinner diameter="18"></mat-spinner>
          <span class="ocr-text">Extracting...</span>
        </div>
      </div>

      <div class="pdf-advance-controls">
        <button
          mat-icon-button
          (click)="toggleWordOverlay()"
          [class.active]="showWordOverlay"
          [disabled]="pdfLoading"
          [matTooltip]="showWordOverlay ? 'Hide OCR word overlay' : 'Show OCR word overlay'"
          tabindex="-1"
        >
          <mat-icon>{{ showWordOverlay ? 'visibility_off' : 'visibility' }}</mat-icon>
        </button>
        <button
          mat-icon-button
          (click)="toggleInBrowserOcr()"
          [class.active]="useInBrowserOcr"
          [disabled]="pdfLoading"
          [matTooltip]="useInBrowserOcr ? 'Disable in-browser OCR' : 'Enable in-browser OCR'"
          tabindex="-1"
        >
          <mat-icon>{{ useInBrowserOcr ? 'memory' : 'text_fields' }}</mat-icon>
        </button>
      </div>
    </div>

    <!-- PDF Canvas Container with OCR Selection -->
    <div class="pdf-canvas-container">
      <!-- Loading State -->
      <div *ngIf="pdfLoading" class="pdf-loading">
        <mat-spinner diameter="50"></mat-spinner>
        <p>Loading PDF...</p>
      </div>

      <!-- PDF Canvas with OCR Selection -->
      <div class="canvas-wrapper" [hidden]="pdfLoading || pdfError" tabindex="-1">
        <div class="pdf-stage-container">
          <div
            class="pdf-page-stage"
            [class.word-overlay-active]="showWordOverlay"
            [class.grid-mode-active]="isGridMode"
            #canvasContainer
            tabindex="-1"
            (contextmenu)="preventCanvasContextMenu($event)"
          >
            <!-- Selection Overlay -->
            <div #selectionOverlay class="selection-overlay"></div>

            <div
              #cellHighlightOverlay
              class="cell-highlight-overlay"
              *ngIf="isGridMode && highlightedCellBounds"
              [style.left.px]="highlightedCellBounds.left"
              [style.top.px]="highlightedCellBounds.top"
              [style.width.px]="highlightedCellBounds.width"
              [style.height.px]="highlightedCellBounds.height"
            ></div>

            <!-- OCR word overlay -->
            <div class="word-overlay" *ngIf="showWordOverlay">
              <ng-container *ngFor="let word of wordOverlayRects; trackBy: trackWordOverlay">
                <div
                  class="word-box"
                  [style.left.px]="word.left"
                  [style.top.px]="word.top"
                  [style.width.px]="word.width"
                  [style.height.px]="word.height"
                ></div>
                <span
                  class="word-box-label"
                  [style.left.px]="word.left"
                  [style.top.px]="word.top - (word.fontSize || 10)"
                  [style.width.px]="word.width"
                  [style.fontSize.px]="word.fontSize || 10"
                  [style.lineHeight.px]="word.fontSize || 10"
                >
                  {{ word.text }}
                  <ng-container *ngIf="word.confidence !== undefined">
                    ({{ word.confidence }})
                  </ng-container>
                </span>
              </ng-container>
            </div>

            <!-- Fabric Canvas Overlay for Grid Mode -->
            <!-- Fabric.js adds a wrapper over this when the grid mode is enabled -->
            <canvas
              #fabricCanvas
              class="fabric-canvas-overlay"
              (contextmenu)="preventCanvasContextMenu($event)"
            ></canvas>
          </div>
        </div>
      </div>

      <!-- No PDF State -->
      <div *ngIf="!pdfUrl && !pdfLoading && !pdfError" class="no-pdf">
        <mat-icon>picture_as_pdf</mat-icon>
        <p>No PDF to display</p>
      </div>
    </div>
  </div>

  <!-- Grid Extraction Panel - Overlay on top of PDF, positioned above content -->
  <div class="grid-panel-overlay" *ngIf="showGridExtractionPanel">
    <div class="grid-panel-header">
      <h3>Grid Extraction Panel</h3>
      <button mat-icon-button (click)="showGridExtractionPanel = false" class="close-btn">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <app-grid-extraction-panel
      [gridData]="gridExtractionPanelData"
      [pageKey]="getCurrentGridPageKey()"
      (rowIgnoreChange)="onGridRowIgnoreChange($event)"
      (importData)="onGridImportData($event)"
    ></app-grid-extraction-panel>
  </div>
</div>
